import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useSelector } from 'react-redux';
import collect from 'collect.js';
import moment from 'moment';
import * as Sentry from '@sentry/react';
import { BasketLineItem, BasketLineItemModifier, StoreInterface } from '../store/types';
import ReduxActions from '../store/ReduxActions';
import { actionCreators } from '../store/actions';
import { EntryType, OfferKindEnum } from '../services/exports/Constants';
import CategoryResource from '../services/resources/CategoryResource';
import useMenu from '../hooks/menu/useMenu';
import { CategoryInterface, MenuInterface, Modifier, Offer, ProductInterface } from '../services/exports/Interfaces';
import useCompanyHours from '../hooks/availability/useCompanyHours';
import useAnalytics from '../hooks/analytics/useAnalytics';
import useDiscounts from '../hooks/order/useDiscounts';
import useCategoryRestriction from '../hooks/menu/useCategoryRestriction';
import toFlatArray from '../utils/to-flat.util';
import { selectCompanyId, selectWorkSchedule } from '../store/slices/InitialDataSlice';
import { CompiledModifier, CompiledProduct, WithActualPrice } from './MenuProvider';
import useGetCompanyMenusApi from '../hooks/api/companies/useGetCompanyMenusApi';
import useOffersStuff from '../features/offers/hooks/useOffersStuff';
import useGetBasketOfferHandlerByKind from '../features/offers/hooks/get-offer-handler/useGetBasketOfferHandlerByKind';

interface Props {
  children: any;
}

export interface BasketContextInterface {
  data: BasketLineItem[];
  items: CompleteBasketItem[];
  originalItems: CompleteBasketItem[];
  removedItems: RemovedBasketItem[];
  isValid: boolean;
  quantity: number;
  subtotal: number;
  actualSubtotal: number;
  total: number;
  basketItemsCount: number;
  compilePreviewItem: (item: BasketLineItem, offers: Offer[]) => CompleteBasketItem;
  compilePreviewProduct: (
    product: WithActualPrice<ProductInterface>,
    offers: Offer[],
    quantity?: number,
  ) => CompleteBasketItem;
  previewOfferSaving: (offer: Offer) => number;
  addItem: (item: BasketLineItem) => boolean;
  addItems: (items: BasketLineItem[]) => boolean;
  updateItem: (basketItemIndex: number, item: BasketLineItem) => void;
  incrementItem: (index: number) => void;
  decrementItem: (index: number) => void;
  clearBasket: () => void;
  refreshBasket: () => void;
  removeUnavailableItem: (removedIndex: number) => void;
  formatForBackend: () => BasketLineItem[];
  formatItemForBackend: (data: BasketLineItem) => BasketLineItem;
  getProductIds: () => number[];
  compileModifier(modifier: BasketLineItemModifier): CompleteBasketItemModifier;
}

export type RemovedBasketItem = {
  lineItem: BasketLineItem;
  product: WithActualPrice<ProductInterface> | null;
  scheduleLabel: string;
  pickupTimeLabel: string;
};

// Per-line breakdown of a free-unit offer (BOGO / free item), attached by the
// offer handlers so the cart + checkout lines can render "N × €X paid · M × free".
// Only set for offers that make whole units free; percentage/amount/half-price
// offers leave it undefined (nothing is "free", just discounted).
export interface OfferLineBreakdown {
  kind: OfferKindEnum;
  paidQuantity: number;
  freeQuantity: number;
  unitPrice: number;
}

export type CompleteBasketItem = Omit<BasketLineItem, 'modifiers'> & {
  entry: WithActualPrice<ProductInterface>;
  menu: MenuInterface;
  category?: CategoryInterface;
  original_price: number;
  actual_price: number;
  is_available: boolean;
  is_sold_out: boolean;
  original_index: number;

  modifiers: CompleteBasketItemModifier[];
  badges: string[];
  offer_breakdown?: OfferLineBreakdown;
};

export type CompleteBasketItemModifier = BasketLineItemModifier & {
  entry: WithActualPrice<Modifier | ProductInterface>;
  original_price: number;
  actual_price: number;
};

export const BasketContext = React.createContext<BasketContextInterface>(null);

export default function BasketProvider(props: Props) {
  const { children } = props;
  const companyId = useSelector(selectCompanyId);
  const workSchedule = useSelector(selectWorkSchedule);
  const { data: menusData } = useGetCompanyMenusApi();
  const { cached_order } = useSelector((state: StoreInterface) => state.order);
  const { lineItems } = useSelector((store: StoreInterface) => store.basket);
  const data: BasketLineItem[] = lineItems[companyId] ?? [];
  const { compileProduct, applyOrderMethodPrices } = useMenu();
  const getRestriction = useCategoryRestriction();
  const { trackAddToBasket, trackAddToBasketFailed, trackRemoveFromBasket } = useAnalytics();
  const { orderTimeAvailable } = useCompanyHours();
  const { calculateDiscount } = useDiscounts();
  const { appliedOffers } = useOffersStuff();
  const getOfferHandler = useGetBasketOfferHandlerByKind();

  const refreshBasketInterval = 30000;
  const [refreshBasketTrigger, setRefreshBasketTrigger] = useState(false);

  useEffect(() => {
    const id = setInterval(() => refreshBasket(), refreshBasketInterval);

    return () => clearInterval(id);
  }, []);

  const formatItemForBackend = (data: BasketLineItem): BasketLineItem => {
    return {
      product_id: data.product_id,
      quantity: data.quantity,
      note: data.note,
      modifiers: data.modifiers,
    };
  };

  const formatForBackend = (): BasketLineItem[] => {
    return items?.map((item) => formatItemForBackend(item));
  };

  const applyPrices = (
    item: Omit<CompleteBasketItem, 'original_price' | 'actual_price' | 'is_sold_out'>,
  ): CompleteBasketItem => {
    if (!item.entry) {
      return {
        ...item,
        is_sold_out: true,
        original_price: 0,
        actual_price: 0,
      };
    }

    const isSoldOut =
      !item.entry.is_available ||
      !!collect(toFlatArray(item.modifiers, 'modifiers')).whereNotNull().firstWhere('entry.is_available', false);

    return {
      ...item,
      is_sold_out: isSoldOut,
      original_price:
        (item.entry.base_price +
          +collect(item.modifiers)
            .whereNotNull()
            .sum((modifier: CompleteBasketItemModifier) => modifier.original_price * modifier.quantity)) *
        item.quantity,
      actual_price:
        (item.entry.actual_price +
          +collect(item.modifiers)
            .whereNotNull()
            .sum((modifier: CompleteBasketItemModifier) => modifier.actual_price * modifier.quantity)) *
        item.quantity,
    };
  };

  const getSortingPosition = (
    entry: CompiledProduct | CompiledModifier,
    lineItemModifier: CompleteBasketItemModifier,
  ) => {
    const groupIndex = entry?.modifier_group_ids?.indexOf(lineItemModifier.modifier_group_id);

    return (
      groupIndex * 1000 +
      collect(entry?.modifier_groups[groupIndex]?.modifier_ids ?? []).search(
        (item) => item.id === lineItemModifier.modifier_id && item.type === lineItemModifier.modifier_type,
      )
    );
  };

  const pureCompileItem = (item: BasketLineItem, originalIndex: number): CompleteBasketItem => {
    const product = compileProduct(item.product_id);
    const categories = collect(product?.category_ids)
      .map((id) => menusData?.categories[id])
      .filter((category) => !!category);
    const menus = collect(menusData?.menus).whereIn('id', categories.pluck('menu_ids').flatten().toArray());

    const findActiveCategoryForMenu = (menuToCheck: MenuInterface): CategoryInterface | undefined =>
      categories.first(
        (category) =>
          category.menu_ids.includes(menuToCheck.id) &&
          new CategoryResource(category, menuToCheck, workSchedule).isActive(cached_order?.scheduled_for),
      );

    const menu = menus.first((menuToCheck) => !!findActiveCategoryForMenu(menuToCheck));
    const category = menu ? findActiveCategoryForMenu(menu) : categories.first();
    const isAvailable = !!menu;

    const modifiers = collect(item.modifiers)
      .map((modifier) => compileModifier(modifier))
      .whereNotNull('modifier')
      .sort((a, b) => getSortingPosition(product, a) - getSortingPosition(product, b));

    return applyPrices({
      ...item,
      entry: product,
      menu: menu ?? menus.first(),
      category,
      modifiers: modifiers.toArray(),
      is_available: orderTimeAvailable ? isAvailable : true,
      original_index: originalIndex,
      badges: [],
    });
  };

  // An offer may only ever lower a line's price. Chaining can otherwise overshoot — a 20%-off
  // offer followed by a free-item offer on a 10.00 line yields 8.00 then -2.00 — and a handler
  // reading an undefined price yields NaN, which would render as "NaN €".
  const clampOfferedPrice = (value: number, previous: number): number =>
    Number.isFinite(value) ? Math.min(Math.max(value, 0), previous) : previous;

  // Builds the price a not-yet-in-basket item would have, by evaluating each offer against the
  // live basket plus the preview item. `original_index: undefined` is the preview sentinel that
  // the free-item handlers branch on.
  //
  // Two invariants that look like redundancy but are not:
  //  1. The handler is rebuilt on every step, so the `items` array it closes over holds the
  //     *current* preview object. `useFreeItemByProducts` identifies the preview by object
  //     identity, so building all handlers up front and folding `apply` over spread copies
  //     silently collapses its discount to zero.
  //  2. `completeItems` is deliberately NOT re-mapped through each offer's `apply` (as
  //     `useWatchUnlockedOffers` does for the basket) — that would be O(offers × basket) per
  //     preview, and no handler's per-item math reads a sibling's discounted price. It reads
  //     `entry.actual_price` (a per-unit menu price `apply` never touches) plus the target
  //     item's own `actual_price`, which this loop already threads.
  const applyPreviewOffers = (previewItem: CompleteBasketItem, offers: Offer[]): CompleteBasketItem => {
    if (!previewItem?.entry || !offers?.length) {
      return previewItem;
    }

    return offers.reduce((carry, offer) => {
      const handler = getOfferHandler(offer, [...completeItems, carry]);

      if (!handler?.apply) {
        return carry;
      }

      const result = handler.apply(carry);

      return { ...result, actual_price: clampOfferedPrice(result.actual_price, carry.actual_price) };
    }, previewItem);
  };

  // Cheap preview for menu cards: the card already holds a compiled, order-method-priced product,
  // and no offer handler reads `menu`, `category`, `is_available`, `original_price` or `modifiers`.
  // Going through `pureCompileItem` for every card would re-resolve categories and build a
  // CategoryResource per menu, and would discard the historical prices `MenuProvider` puts on
  // "order again" cards.
  const toPreviewItem = (product: WithActualPrice<ProductInterface>, quantity: number): CompleteBasketItem => ({
    product_id: product?.id,
    quantity,
    note: '',
    modifiers: [],
    entry: product,
    menu: undefined,
    category: undefined,
    original_price: product?.base_price * quantity,
    actual_price: product?.actual_price * quantity,
    is_available: true,
    is_sold_out: false,
    original_index: undefined,
    badges: [],
  });

  const compilePreviewItem = (item: BasketLineItem, offers: Offer[]): CompleteBasketItem =>
    applyPreviewOffers(pureCompileItem(item, undefined), offers);

  const compilePreviewProduct = (
    product: WithActualPrice<ProductInterface>,
    offers: Offer[],
    quantity = 1,
  ): CompleteBasketItem => applyPreviewOffers(toPreviewItem(product, quantity), offers);

  const getPriceBy = (modifier: BasketLineItemModifier, by: string): number => {
    const entry =
      modifier.modifier_type === EntryType.Product
        ? menusData?.products?.[modifier.modifier_id]
        : menusData?.modifiers?.[modifier.modifier_id];
    const data = applyOrderMethodPrices(entry ?? null);

    let result: number = data[by] ?? 0;

    modifier?.modifiers?.forEach((nestedModifier) => {
      result += getPriceBy(nestedModifier, by) * nestedModifier.quantity;
    });

    return result;
  };

  const compileModifier = (modifier: BasketLineItemModifier): CompleteBasketItemModifier => {
    const entry =
      modifier.modifier_type === EntryType.Product
        ? menusData?.products?.[modifier.modifier_id]
        : menusData?.modifiers?.[modifier.modifier_id];
    const data = applyOrderMethodPrices(entry ?? null);

    const originalPrice = getPriceBy(modifier, 'base_price');
    const actualPrice = getPriceBy(modifier, 'actual_price');
    const modifiers = modifier.modifiers?.length
      ? modifier.modifiers.map((nestedModifier) => compileModifier(nestedModifier))
      : [];

    return {
      ...modifier,
      entry: data,
      original_price: originalPrice,
      actual_price: actualPrice,
      modifiers,
    };
  };

  // Items whose source category is currently outside its window for the active pickup time.
  // Derived from the live `data` + restriction view, so changing pickup time flips items in
  // and out without any dispatches.
  const restrictedIndices = useMemo<number[]>(() => {
    if (!menusData?.categories || !menusData?.products) {
      return [];
    }

    return data.reduce<number[]>((acc, lineItem, index) => {
      const product = compileProduct(lineItem.product_id);
      const { isRestricted } = getRestriction(product?.category_ids);

      if (isRestricted) {
        acc.push(index);
      }

      return acc;
    }, []);
  }, [data, menusData?.categories, menusData?.products, compileProduct, getRestriction]);

  const restrictedIndexSet = useMemo(() => new Set(restrictedIndices), [restrictedIndices]);

  const completeItems = useMemo<CompleteBasketItem[]>(
    () =>
      collect(data)
        .map((item, index) => pureCompileItem(item, index))
        .filter((item) => !item.is_sold_out)
        .filter((item) => !!item.entry && !!item.menu)
        .filter((item) => !item.modifiers.length || Boolean(item.modifiers[0]?.modifier_type))
        .filter((item) => !restrictedIndexSet.has(item.original_index))
        .whereNotNull('product')
        .toArray(),
    [
      data,
      menusData?.products,
      menusData?.modifiers,
      menusData?.categories,
      menusData?.menus,
      cached_order?.scheduled_for,
      cached_order?.coupon_type,
      cached_order?.method,
      refreshBasketTrigger,
      restrictedIndexSet,
    ],
  );
  const items = useMemo(() => {
    if (!appliedOffers?.length) {
      return completeItems;
    }

    return appliedOffers.reduce((result, appliedOffer) => {
      const offerHandler = getOfferHandler(appliedOffer, result);

      if (!offerHandler?.apply) {
        return result;
      }

      return result.map(offerHandler.apply);
    }, completeItems);
  }, [completeItems, appliedOffers]);

  // The € a NOT-yet-applied offer would save against the current basket — used by the
  // comparison-choice (offer modal) so BEST CHOICE can compare it to an active discount. Mirrors
  // how `items`/`discount` account for offers: apply-type handlers (BOGO/free item) reduce line
  // prices (delta), profit-type handlers (%/amount off) report `profit`.
  const previewOfferSaving = useCallback(
    (offer: Offer): number => {
      const handler = getOfferHandler(offer, completeItems);

      if (!handler) {
        return 0;
      }

      if (!handler.apply) {
        return Math.max(0, handler.profit ?? 0);
      }

      const previewed = completeItems.map(handler.apply);
      const base = +collect(completeItems).sum('actual_price');
      const withOffer = +collect(previewed).sum('actual_price');

      return Math.max(0, base - withOffer);
    },
    [completeItems, getOfferHandler],
  );

  const modifierHash = (modifiers: BasketLineItemModifier[]): string =>
    collect(toFlatArray(modifiers, 'modifiers'))
      .sortBy(
        (modifier: BasketLineItemModifier) =>
          `${modifier.modifier_group_id}-${modifier.modifier_id}-${modifier.modifier_type}-${modifier.quantity}`,
      )
      .reduce(
        (carry: string, modifier: BasketLineItemModifier) =>
          carry +
          `${modifier.modifier_group_id}-${modifier.modifier_id}-${modifier.modifier_type}-${modifier.quantity}`,
        '',
      );

  // Searches raw `data` (not `items`), so sold-out rows can still be merged into.
  // Returns the index into `data`.
  const searchItem = useCallback(
    (item: BasketLineItem): number => {
      if (!companyId || data.length === 0) {
        return null;
      }

      const inputHash = modifierHash(item.modifiers ?? []);

      return collect(data).search(
        (lineItem) =>
          item.product_id === lineItem.product_id &&
          item.note === lineItem.note &&
          inputHash === modifierHash(lineItem.modifiers ?? []),
      );
    },
    [data, companyId],
  );

  const addItem = (item: BasketLineItem): boolean => {
    if (!companyId) {
      trackAddToBasketFailed({ reason: 'missing_company_id', product_id: item?.product_id });

      return false;
    }

    try {
      const dataIndex = searchItem(item);

      if (typeof dataIndex === 'number' && dataIndex >= 0) {
        const existing = data[dataIndex];
        const newQuantity = existing.quantity + item.quantity;

        ReduxActions.dispatch(
          actionCreators.basket.updateItem({
            company_id: companyId,
            index: dataIndex,
            item: { quantity: newQuantity },
          }),
        );

        trackAddToBasket({
          ...pureCompileItem({ ...existing, quantity: newQuantity }, dataIndex),
          quantity: newQuantity,
        });

        return true;
      }

      ReduxActions.dispatch(actionCreators.basket.addItem({ company_id: companyId, item }));
      trackAddToBasket(pureCompileItem(item, 0));

      return true;
    } catch (error) {
      Sentry.captureException(error, { tags: { flow: 'add_to_basket' } });
      trackAddToBasketFailed({ reason: 'exception', product_id: item?.product_id, message: error?.message });

      return false;
    }
  };

  const addItems = (items: BasketLineItem[]): boolean => {
    if (!companyId) {
      trackAddToBasketFailed({ reason: 'missing_company_id', product_id: items?.[0]?.product_id });

      return false;
    }

    try {
      ReduxActions.dispatch(actionCreators.basket.addItems({ company_id: companyId, items }));
      Promise.all(items.map((item) => trackAddToBasket(pureCompileItem(item, 0))));

      return true;
    } catch (error) {
      Sentry.captureException(error, { tags: { flow: 'add_to_basket' } });
      trackAddToBasketFailed({ reason: 'exception', product_id: items?.[0]?.product_id, message: error?.message });

      return false;
    }
  };

  const updateItem = (basketItemIndex: number, updatedBasketItem: BasketLineItem): void => {
    const item = items[basketItemIndex];

    ReduxActions.dispatch(
      actionCreators.basket.updateItem({
        company_id: companyId,
        index: item.original_index,
        item: updatedBasketItem,
      }),
    );

    trackAddToBasket(pureCompileItem(updatedBasketItem, item.original_index));
  };

  const incrementItem = (index: number, quantity: number = 1): void => {
    if (!companyId) {
      return;
    }

    const item = items[index];

    ReduxActions.dispatch(
      actionCreators.basket.updateItem({
        company_id: companyId,
        index: item.original_index,
        item: { quantity: item.quantity + quantity },
      }),
    );

    trackAddToBasket({
      ...pureCompileItem({ ...item, quantity: item.quantity + quantity }, index),
      quantity: item.quantity + quantity,
    });
  };

  const decrementItem = (index: number): void => {
    if (!companyId) {
      return;
    }

    const item = items[index];

    item.quantity === 1
      ? ReduxActions.dispatch(actionCreators.basket.removeItem({ company_id: companyId, index }))
      : ReduxActions.dispatch(
          actionCreators.basket.updateItem({
            company_id: companyId,
            index: item.original_index,
            item: { quantity: item.quantity - 1 },
          }),
        );

    trackRemoveFromBasket({
      ...pureCompileItem({ ...item, quantity: item.quantity - 1 }, index),
      quantity: item.quantity - 1,
    });
  };

  const basketItemsCount = useMemo(() => items?.reduce((acc, item) => acc + item.quantity, 0), [items]);

  const clearBasket = () => companyId && ReduxActions.dispatch(actionCreators.basket.clear({ company_id: companyId }));

  const removedItems = useMemo<RemovedBasketItem[]>(() => {
    return restrictedIndices.map((dataIndex) => {
      const lineItem = data[dataIndex];
      const product = compileProduct(lineItem.product_id);
      const { scheduleLabel } = getRestriction(product?.category_ids);
      const pickupTimeLabel = cached_order?.scheduled_for ? moment(cached_order.scheduled_for).format('HH:mm') : '';

      return { lineItem, product, scheduleLabel, pickupTimeLabel };
    });
  }, [restrictedIndices, data, compileProduct, getRestriction, cached_order?.scheduled_for]);

  // `removedIndex` is the position in `removedItems`. We translate it back to the line
  // item's index in `data` before dispatching the actual remove.
  const removeUnavailableItem = (removedIndex: number) => {
    if (!companyId) {
      return;
    }

    const dataIndex = restrictedIndices[removedIndex];

    if (dataIndex === undefined) {
      return;
    }

    ReduxActions.dispatch(actionCreators.basket.removeItem({ company_id: companyId, index: dataIndex }));
  };

  const refreshBasket = () => setRefreshBasketTrigger((prev) => !prev);

  const getProductIds = (): number[] =>
    collect(items).pluck('product_id').merge(collect(items).pluck('children.product_id')).toArray();

  const isValid = useMemo(() => !collect(items).first((item) => item.is_sold_out), [items]);

  const quantity = useMemo<number>(() => +collect(items).sum('quantity'), [items]);

  const subtotal = useMemo<number>(() => +collect(completeItems).sum('original_price'), [completeItems]);
  const actualSubtotal = useMemo<number>(() => +collect(completeItems).sum('actual_price'), [completeItems]);

  const discount = useMemo<number>(() => {
    const discount = calculateDiscount(actualSubtotal);

    if (!appliedOffers?.length) {
      return discount;
    }

    const itemsDiscount = actualSubtotal - +collect(items).sum('actual_price');

    const offerHandlers = appliedOffers
      .map((appliedOffer) => getOfferHandler(appliedOffer, completeItems))
      .filter((handler) => !handler?.apply);

    return offerHandlers.reduce((result, handler) => result + handler.profit, discount + itemsDiscount);
  }, [cached_order?.coupon_type, cached_order?.coupon_details, actualSubtotal, appliedOffers, completeItems, items]);

  const total = useMemo<number>(() => Math.max(0, actualSubtotal - discount), [actualSubtotal, discount]);

  const context: BasketContextInterface = {
    data,
    items,
    originalItems: completeItems,
    removedItems,
    isValid,
    quantity,
    subtotal,
    actualSubtotal,
    total,
    basketItemsCount,
    compilePreviewItem,
    compilePreviewProduct,
    previewOfferSaving,
    addItem,
    incrementItem,
    decrementItem,
    clearBasket,
    refreshBasket,
    removeUnavailableItem,
    formatForBackend,
    formatItemForBackend,
    getProductIds,
    compileModifier,
    updateItem,
    addItems,
  };

  return <BasketContext.Provider value={context}>{children}</BasketContext.Provider>;
}
